import { useEffect, useMemo, useState } from "react";
import {
  fetchPricelistById,
  fetchPricelistList,
  type Pricelist,
} from "../api/pricelist";
import { useCurrentUserInfo } from "./useCurrentUserInfo";
import { useUserRoles } from "./useUserRoles";
import { getTariffAccessState, type TariffAccessState } from "../utils/tariffAccess";

const collectPricelistIds = (pricelistIds: Array<string | undefined>): string[] =>
  [...new Set(pricelistIds.map((id) => id?.trim()).filter((id): id is string => Boolean(id)))];

export const useTariffAccess = (): TariffAccessState => {
  const {
    activeTariffs,
    requestedTariffs,
    userLimits,
    loading,
    resolved,
    policySetting,
  } = useCurrentUserInfo();
  const { isAdmin } = useUserRoles();
  const [pricelists, setPricelists] = useState<Pricelist[]>([]);
  const [pricelistsResolved, setPricelistsResolved] = useState(false);

  const hasUserLimitsModel = userLimits != null;
  const pricelistIds = useMemo(
    () => collectPricelistIds(activeTariffs.map((tariff) => tariff.pricelistId)),
    [activeTariffs]
  );
  const pricelistIdsKey = pricelistIds.join(",");

  useEffect(() => {
    if (hasUserLimitsModel) {
      setPricelists([]);
      setPricelistsResolved(true);
      return;
    }

    if (!resolved || loading) {
      setPricelistsResolved(false);
      return;
    }

    const ids = collectPricelistIds(pricelistIdsKey ? pricelistIdsKey.split(",") : []);
    if (!ids.length) {
      setPricelists([]);
      setPricelistsResolved(true);
      return;
    }

    let cancelled = false;
    setPricelistsResolved(false);

    const load = async () => {
      const byId = await Promise.all(
        ids.map((id) =>
          fetchPricelistById(id)
            .then((item) => item)
            .catch(() => null)
        )
      );
      const found = byId.filter((item): item is Pricelist => item != null);
      if (found.length === ids.length) {
        return found;
      }

      const list = await fetchPricelistList().catch(() => [] as Pricelist[]);
      const byListId = new Map(list.map((item) => [item.id, item]));
      return ids
        .map((id) => found.find((item) => item.id === id) ?? byListId.get(id) ?? null)
        .filter((item): item is Pricelist => item != null);
    };

    load()
      .then((items) => {
        if (!cancelled) {
          setPricelists(items);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPricelistsResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasUserLimitsModel, loading, pricelistIdsKey, resolved]);

  return useMemo(() => {
    const state = getTariffAccessState(
      activeTariffs,
      loading,
      resolved,
      pricelists,
      pricelistsResolved,
      policySetting,
      userLimits,
      requestedTariffs
    );

    if (isAdmin) {
      return {
        ...state,
        canRunChecks: true,
        canPolicyCheck: true,
        canItCheck: true,
        canScanBySchedule: true,
        canEmailReport: true,
      };
    }

    return state;
  }, [
    activeTariffs,
    isAdmin,
    loading,
    policySetting,
    pricelists,
    pricelistsResolved,
    requestedTariffs,
    resolved,
    userLimits,
  ]);
};
